// Import the locale primitives from the CLIENT-SAFE `./locales` module, not
// `./i18n` — the latter pulls `next/headers` (server-only), which breaks the
// build when `locale-detect` is imported into a client component (e.g. the
// visitor-auth handshake). `./locales` exports the same three symbols.
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from "./locales";

/**
 * Resolve the active locale on the client. Used by the widget shell
 * to set `NextIntlClientProvider`'s `locale` on mount.
 *
 * Resolution order:
 *
 *   1. `?lang=` URL search param          — explicit dev / marketing override
 *   2. `wexio-locale` cookie              — persisted explicit override
 *   3. operator `localeStrategy`:
 *        - a pinned code (`en` / `uk`)    — use it
 *        - `WEBSITE`                       — host page `<html lang>`
 *        - `AUTO` (or unset)               — fall through to the browser
 *   4. `navigator.language`               — browser preference
 *   5. `DEFAULT_LOCALE`
 *
 * `strategy` comes from the operator's BE config (`WidgetConfig.localeStrategy`).
 * It's the operator's choice of HOW to pick the visitor's language; the
 * explicit `?lang=` / cookie signals normally sit above it as dev / visitor
 * escape hatches.
 *
 * `options.ignoreVisitorOverrides` flips that order: skip the `?lang=` and
 * cookie checks so the strategy wins outright. The React-component embed
 * passes this in `preview` mode — the operator is iterating on their
 * locale-strategy dropdown and expects the widget to reflect THEIR draft,
 * not whatever locale cookie their dashboard browser happens to hold.
 *
 * Every raw value is normalised the same way: lower-cased + `_` → `-`, with
 * the full BCP-47 tag tried first (so `pt-BR` / `zh-TW` resolve to themselves
 * when supported) before falling back to the base language.
 */
export function detectClientLocale(
  strategy?: string,
  options?: { ignoreVisitorOverrides?: boolean },
): SupportedLocale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  if (!options?.ignoreVisitorOverrides) {
    // 1. Explicit `?lang=` override — wins over everything (dev, marketing
    //    tour), unless the caller asked to ignore visitor overrides (preview).
    const url = new URL(window.location.href);
    const fromUrl = matchLocale(url.searchParams.get("lang"));
    if (fromUrl) return fromUrl;

    // 2. Persisted explicit override (the cookie middleware sets from `?lang=`,
    //    the Profile tab's language-switcher sets it on save).
    const fromCookie = matchLocale(readCookie("wexio-locale"));
    if (fromCookie) return fromCookie;
  }

  // 3. Operator strategy.
  if (strategy && strategy !== "AUTO" && strategy !== "WEBSITE") {
    // Pinned to a specific language. If the operator pinned a code the
    // widget bundle doesn't ship, fall through rather than render a
    // broken/untranslated UI.
    const pinned = matchLocale(strategy);
    if (pinned) return pinned;
  } else if (strategy === "WEBSITE") {
    // Match the host page — `<html lang="…">`, which a site's own
    // language switcher keeps in sync. The widget shares the host
    // document in web-component / react-component mode, so this reads
    // the host's attribute directly. Falls through to the browser when
    // the page declares no (supported) language.
    const fromPage = matchLocale(document.documentElement.lang);
    if (fromPage) return fromPage;
  }

  // 4. Browser preference — also the `AUTO` path.
  const fromNav = matchLocale(navigator.language);
  if (fromNav) return fromNav;

  return DEFAULT_LOCALE;
}

// Lower-cased lookup → canonical form, e.g. `"pt-br"` → `"pt-BR"`. Built once
// from `SUPPORTED_LOCALES` so the matcher can be case-insensitive without
// scanning the array per call.
const CANONICAL_BY_LOWER: ReadonlyMap<string, SupportedLocale> = new Map(
  (SUPPORTED_LOCALES as readonly SupportedLocale[]).map((code) => [code.toLowerCase(), code]),
);

/**
 * Normalise a raw locale-ish string (`"uk-UA"`, `"UK_uk"`, `"en"`,
 * `"pt-BR"`) and return the canonical supported code. Tries the full tag
 * first (so `pt-BR` doesn't collapse to `pt`, `zh-TW` to `zh`), then falls
 * back to the base language. `null` for empty / unsupported input.
 */
function matchLocale(raw: string | null | undefined): SupportedLocale | null {
  if (!raw) return null;
  const lower = raw.toLowerCase().replace(/_/g, "-");
  const full = CANONICAL_BY_LOWER.get(lower);
  if (full) return full;
  const base = lower.split("-")[0];
  return CANONICAL_BY_LOWER.get(base) ?? null;
}

function readCookie(name: string): string | null {
  const cookies = document.cookie.split(";");
  for (const raw of cookies) {
    const [key, ...rest] = raw.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}
